/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Notifications pane. Controlled by the host (web `PreferencesStore`, desktop
// preferences controller) via `NotificationsPaneProps`; when a prop is omitted
// the row falls back to local state so the standalone SettingsView surface
// still renders. Toggles use the shared `Toggle` primitive (Figma 3542:5278);
// the preview + quiet-hours selectors reuse the shared `Select` chip pattern.

import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { Select, Toggle } from "../controls";
import { SettingsDivider, SettingsRow } from "../SettingsRow";

const PREVIEW_OPTIONS = [
	{ value: "full" as const, label: "Full (sender + message)" },
	{ value: "name" as const, label: "Sender name only" },
	{ value: "none" as const, label: "None" },
];

type PreviewOption = (typeof PREVIEW_OPTIONS)[number]["value"];

// Half-hour quiet-hours options (00:00 … 23:30).
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
	const h = Math.floor(i / 2);
	const m = i % 2 === 0 ? "00" : "30";
	const value = `${String(h).padStart(2, "0")}:${m}`;
	return { value, label: value };
});

export interface NotificationsPaneProps {
	readonly notificationsEnabled?: boolean;
	readonly onNotificationsEnabledChange?: (next: boolean) => void;
	readonly osNotificationsEnabled?: boolean;
	readonly onOsNotificationsEnabledChange?: (next: boolean) => void;
	/** True when OS-level permission is already granted. When false and OS
	 *  notifications are enabled, the pane shows an "Enable…" CTA. */
	readonly osPermissionGranted?: boolean;
	/** True when the OS/browser has BLOCKED notifications. The browser won't
	 *  re-prompt, so the pane shows unblock guidance instead of a dead button. */
	readonly osPermissionBlocked?: boolean;
	readonly onRequestOsPermission?: () => void;
	readonly preview?: PreviewOption;
	readonly onPreviewChange?: (next: PreviewOption) => void;
	readonly inAppSounds?: boolean;
	readonly onInAppSoundsChange?: (next: boolean) => void;
	readonly notifyOnlyWhenUnfocused?: boolean;
	readonly onNotifyOnlyWhenUnfocusedChange?: (next: boolean) => void;
	readonly dndEnabled?: boolean;
	readonly onDndEnabledChange?: (next: boolean) => void;
	readonly dndStart?: string;
	readonly dndEnd?: string;
	readonly onDndWindowChange?: (start: string, end: string) => void;
	/** Number of conversations currently muted (display-only). */
	readonly mutedCount?: number;
}

export function NotificationsPane(props: NotificationsPaneProps = {}): JSX.Element {
	// Local fallback state for the standalone (uncontrolled) SettingsView usage.
	const [localEnabled, setLocalEnabled] = useState(true);
	const [localOsEnabled, setLocalOsEnabled] = useState(true);
	const [localPreview, setLocalPreview] = useState<PreviewOption>("full");
	const [localSounds, setLocalSounds] = useState(true);
	const [localUnfocusedOnly, setLocalUnfocusedOnly] = useState(true);
	const [localDnd, setLocalDnd] = useState(false);
	const [localDndStart, setLocalDndStart] = useState("22:00");
	const [localDndEnd, setLocalDndEnd] = useState("08:00");

	const enabled = props.notificationsEnabled ?? localEnabled;
	const setEnabled = props.onNotificationsEnabledChange ?? setLocalEnabled;
	const osEnabled = props.osNotificationsEnabled ?? localOsEnabled;
	const setOsEnabled = props.onOsNotificationsEnabledChange ?? setLocalOsEnabled;
	const preview = props.preview ?? localPreview;
	const setPreview = props.onPreviewChange ?? setLocalPreview;
	const inAppSounds = props.inAppSounds ?? localSounds;
	const setInAppSounds = props.onInAppSoundsChange ?? setLocalSounds;
	const unfocusedOnly = props.notifyOnlyWhenUnfocused ?? localUnfocusedOnly;
	const setUnfocusedOnly = props.onNotifyOnlyWhenUnfocusedChange ?? setLocalUnfocusedOnly;
	const dnd = props.dndEnabled ?? localDnd;
	const setDnd = props.onDndEnabledChange ?? setLocalDnd;
	const dndStart = props.dndStart ?? localDndStart;
	const dndEnd = props.dndEnd ?? localDndEnd;
	const setDndWindow =
		props.onDndWindowChange ??
		((s: string, e: string) => {
			setLocalDndStart(s);
			setLocalDndEnd(e);
		});

	const showPermissionCta =
		osEnabled &&
		props.osPermissionGranted === false &&
		props.osPermissionBlocked !== true &&
		props.onRequestOsPermission !== undefined;
	const showBlockedHint =
		osEnabled && props.osPermissionGranted === false && props.osPermissionBlocked === true;

	return (
		<View className="flex flex-col gap-4">
			<SettingsRow
				variant="hero"
				title="Notifications"
				description="Control how Ping alerts you while it's running."
				testID="settings-notifications-intro"
			/>
			<SettingsRow
				title="Enable notifications"
				description="Master switch — turn off to silence every alert on this device."
				testID="settings-notifications-enable"
			>
				<View className="flex flex-row">
					<Toggle checked={enabled} onChange={setEnabled} />
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Background alerts"
				description="Show a system notification when Ping is in the background or another app is focused."
				testID="settings-notifications-background"
			>
				<View className="flex flex-col items-end gap-2">
					<Toggle checked={osEnabled} onChange={setOsEnabled} disabled={!enabled} />
					{showPermissionCta ? (
						<Pressable
							onPress={props.onRequestOsPermission}
							className="rounded-md bg-[#537EFF] px-2.5 py-1"
						>
							<Text className="text-[11px] font-semibold text-white">Enable in system</Text>
						</Pressable>
					) : null}
					{showBlockedHint ? (
						<Text className="max-w-[200px] text-right text-[11px] leading-[15px] text-[#E5704B]">
							Blocked by your browser. Open the site settings (lock icon in the
							address bar) and allow Notifications, then reload.
						</Text>
					) : null}
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Show message in notification"
				description="How much of an incoming message appears in alerts."
				testID="settings-notifications-preview"
			>
				<View className="flex flex-row">
					<Select
						value={preview}
						onChange={setPreview}
						options={PREVIEW_OPTIONS}
						disabled={!enabled}
					/>
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="In-app sounds"
				description="Play a chime for new messages and mentions."
				testID="settings-notifications-sounds"
			>
				<View className="flex flex-row">
					<Toggle checked={inAppSounds} onChange={setInAppSounds} disabled={!enabled} />
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Only when not viewing"
				description="Stay silent for the conversation you're currently looking at."
			>
				<View className="flex flex-row">
					<Toggle checked={unfocusedOnly} onChange={setUnfocusedOnly} disabled={!enabled} />
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Quiet hours"
				description="Silence non-mention alerts during a daily window. @mentions still come through."
				testID="settings-notifications-quiet"
			>
				<View className="flex flex-col items-end gap-2">
					<Toggle checked={dnd} onChange={setDnd} disabled={!enabled} />
					{dnd ? (
						<View className="flex flex-row items-center gap-1.5">
							<Select
								value={dndStart}
								onChange={(next) => setDndWindow(next, dndEnd)}
								options={TIME_OPTIONS}
								disabled={!enabled}
							/>
							<Text className="text-[12px] text-light-text-secondary dark:text-[#B5BAC3]">
								to
							</Text>
							<Select
								value={dndEnd}
								onChange={(next) => setDndWindow(dndStart, next)}
								options={TIME_OPTIONS}
								disabled={!enabled}
							/>
						</View>
					) : null}
				</View>
			</SettingsRow>

			{props.mutedCount !== undefined && props.mutedCount > 0 ? (
				<>
					<SettingsDivider />
					<SettingsRow
						title="Muted chats"
						description="Mute individual conversations from each chat's header menu."
					>
						<Text className="text-[12px] text-light-text-secondary dark:text-[#B5BAC3]">
							{props.mutedCount} muted
						</Text>
					</SettingsRow>
				</>
			) : null}
		</View>
	);
}
